/**
 * Deterministic colour for a route id. Port of `uniqolor` (MIT) with its default
 * options so that every trail keeps the colour it had on the historical site.
 */
function hashCode(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash &= hash; // 32-bit
    }
    return hash;
}

function hueToRgb(p: number, q: number, t: number): number {
    if (t < 0) t += 1;
    else if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
    h /= 360;
    s /= 100;
    l /= 100;
    if (s === 0) {
        const v = Math.round(l * 255);
        return [v, v, v];
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    return [
        Math.round(hueToRgb(p, q, h + 1 / 3) * 255),
        Math.round(hueToRgb(p, q, h) * 255),
        Math.round(hueToRgb(p, q, h - 1 / 3) * 255),
    ];
}

const hex2 = (n: number) => n.toString(16).padStart(2, '0');

/** `#rrggbb` colour, saturation in [50, 55), lightness in [50, 60). */
export function colorFor(value: string): string {
    const hash = Math.abs(hashCode(value));
    const h = hash % 360;
    const s = (hash % 5) + 50;
    const l = (hash % 10) + 50;
    const [r, g, b] = hslToRgb(h, s, l);
    return `#${hex2(r)}${hex2(g)}${hex2(b)}`;
}
